/* bb-budget.util.js — DROP-IN REPLACEMENT (robust KV parsing + CSV) */
;(() => {
  "use strict";

  window.TYBH = window.TYBH || {};
  const TYBH = window.TYBH;

  // Simple ID helper (kept for compatibility)
  const $ = (id) => document.getElementById(id);

  function escapeHtml(s){
    return String(s ?? "")
      .replace(/&/g,"&amp;")
      .replace(/</g,"&lt;")
      .replace(/>/g,"&gt;")
      .replace(/"/g,"&quot;");
  }
  function escapeAttr(s){
    return String(s ?? "")
      .replace(/&/g,"&amp;")
      .replace(/</g,"&lt;")
      .replace(/>/g,"&gt;")
      .replace(/"/g,"&quot;")
      .replace(/'/g,"&#39;");
  }
  function stripBom(s){ return String(s ?? "").replace(/^\uFEFF/, ""); }

  function isoToday(){
    const d = new Date();
    const z = new Date(d.getTime() - d.getTimezoneOffset()*60000);
    return z.toISOString().slice(0,10);
  }
  function ymToday(){
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth()+1).padStart(2,"0");
    return `${y}-${m}`;
  }
  function toMonth(dateStr){
    const s = String(dateStr||"").slice(0,10);
    return s ? s.slice(0,7) : "";
  }

  function num(v){
    if(v === null || v === undefined) return 0;
    if(typeof v === "number") return isFinite(v) ? v : 0;

    let s = String(v).trim();
    if(!s) return 0;

    let neg = false;
    if(/^\(.*\)$/.test(s)){
      neg = true;
      s = s.slice(1, -1).trim();
    }

    s = s.replace(/,/g, "");
    const m = s.match(/-?\d+(\.\d+)?/);
    if(!m) return 0;

    const n = Number(m[0]);
    if(!isFinite(n)) return 0;
    return neg ? -Math.abs(n) : n;
  }

  // keeps your old signature money(n)
  function money(n){
    const c =
      (window.TYBH && window.TYBH.state && window.TYBH.state.settings && window.TYBH.state.settings.currency)
      || "USD";
    const x = Number(n || 0);
    try{
      return x.toLocaleString(undefined,{style:"currency",currency:c});
    }catch(e){
      return x.toFixed(2) + " " + c;
    }
  }

  // slightly more collision-resistant but same spirit
  function uid(){
    const t = Date.now().toString(16).slice(-6).toUpperCase();
    const r = Math.random().toString(16).slice(2,10).toUpperCase();
    return "TX-" + t + "-" + r;
  }

  function cleanCell(s){ return String(s ?? "").replace(/\r?\n/g," ").trim(); }

  function monthLabel(m){
    const s = String(m||"").trim();
    if(!/^\d{4}-\d{2}$/.test(s)) return s || "All";
    const y = s.slice(0,4);
    const mm = Number(s.slice(5,7));
    const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const name = names[Math.max(1,Math.min(12,mm))-1] || s;
    return `${s} (${name} ${y})`;
  }

  function uniq(arr){
    return Array.from(new Set((arr||[]).map(x=>String(x||"").trim()).filter(Boolean)));
  }

  // CSV parser (handles \n, \r\n, \r; quotes; escaped quotes)
  function parseCsv(text){
    text = stripBom(String(text || ""));
    const rows = [];
    let row = [];
    let cur = "";
    let inQuotes = false;

    for(let i=0;i<text.length;i++){
      const ch = text[i];
      const next = text[i+1];

      if(inQuotes){
        if(ch === '"' && next === '"'){ cur += '"'; i++; continue; }
        if(ch === '"'){ inQuotes = false; continue; }
        cur += ch;
        continue;
      }

      if(ch === '"'){ inQuotes = true; continue; }

      if(ch === ","){ row.push(cur); cur=""; continue; }

      if(ch === "\n" || ch === "\r"){
        // swallow \r\n as one newline
        if(ch === "\r" && next === "\n"){ i++; }
        row.push(cur); cur="";
        // skip pure blank line
        if(!(row.length===1 && String(row[0]||"")==="")){
          rows.push(row.map(x=>String(x).replace(/\r$/,"")));
        }
        row = [];
        continue;
      }

      cur += ch;
    }

    row.push(cur);
    if(row.length>1 || (row.length===1 && String(row[0]||"").trim()!=="")){
      rows.push(row.map(x=>String(x).replace(/\r$/,"")));
    }

    if(rows[0] && rows[0][0]) rows[0][0] = String(rows[0][0]).replace(/^\uFEFF/,"");
    return rows;
  }

  function toCsv(rows){
    const bom = "\uFEFF";
    const safeRows = Array.isArray(rows) ? rows : [];
    const lines = safeRows.map(r=>{
      const rr = Array.isArray(r) ? r : [];
      return rr.map(cell=>{
        const s = cleanCell(cell);
        const needs = /[",\n\r]/.test(s);
        const esc = s.replace(/"/g,'""');
        return needs ? `"${esc}"` : esc;
      }).join(",");
    });
    return bom + lines.join("\n");
  }
  function toCsvNoBom(rows){
    return toCsv(rows).replace(/^\uFEFF/,"");
  }

  // MASTER parser: splits #TABLE:Name blocks into raw CSV text
  function parseMaster(text){
    const lines = stripBom(String(text||"")).replace(/\r\n/g,"\n").replace(/\r/g,"\n").split("\n");
    const blocks = {};
    let cur = null;
    let buf = [];

    function flush(){
      if(cur){
        // keep content as-is but avoid trailing extra newlines
        blocks[cur] = buf.join("\n").replace(/\s+$/,"");
      }
      buf = [];
    }

    for(const line of lines){
      if(line.startsWith("#TABLE:")){
        flush();
        cur = line.slice(7).trim();
        continue;
      }
      if(cur !== null) buf.push(line);
    }
    flush();
    return blocks;
  }

  // IMPORTANT: join columns 1..end (so comma-lists like "Eat Out, Dining, Takeout" survive)
  function readKeyValueBlock(csvText){
    const rows = parseCsv(csvText);
    const map = {};
    for(let i=1;i<rows.length;i++){
      const r = rows[i] || [];
      const k = String(r[0]||"").trim();
      const v = r.length <= 2
        ? String(r[1]||"").trim()
        : r.slice(1).join(",").trim();
      if(k) map[k] = v;
    }
    return map;
  }

  function pickFirstMatch(list, keywords){
    const ks = (keywords||[]).map(x=>String(x||"").toLowerCase()).filter(Boolean);
    for(const item of (list||[])){
      const s = String(item||"").toLowerCase();
      if(ks.some(k => s===k || s.includes(k))) return item;
    }
    return "";
  }
  function parseKeywords(s){
    return String(s||"")
      .split(",")
      .map(x=>x.trim().toLowerCase())
      .filter(Boolean);
  }
  function catMatches(cat, keywords){
    const c = String(cat||"").toLowerCase();
    return (keywords||[]).some(k=>k && (c===k || c.includes(k)));
  }

  function startOfWeek(d){
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = x.getDay(); // 0 Sun ... 6 Sat
    const diff = (day===0 ? -6 : 1-day); // Monday start
    x.setDate(x.getDate()+diff);
    x.setHours(0,0,0,0);
    return x;
  }
  function endOfWeek(d){
    const s = startOfWeek(d);
    const e = new Date(s);
    e.setDate(e.getDate()+6);
    e.setHours(23,59,59,999);
    return e;
  }
  function isoToDate(dateStr){
    const s = String(dateStr||"").slice(0,10);
    if(!/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date();
    const [y,m,dd] = s.split("-").map(n=>Number(n));
    return new Date(y, m-1, dd);
  }

  function daysLeftInMonth(fromDate){
    const d = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const last = new Date(d.getFullYear(), d.getMonth()+1, 0);
    const diffMs = (last.setHours(23,59,59,999) - d.setHours(0,0,0,0));
    const days = Math.floor(diffMs / (24*3600*1000)) + 1;
    return Math.max(1, days);
  }
  function daysInMonth(y, mIndex){
    return new Date(y, mIndex+1, 0).getDate();
  }

  function downloadText(filename, text, mime){
    const type = mime || (String(filename||"").toLowerCase().endsWith(".csv") ? "text/csv" : "text/plain");
    const blob = new Blob([String(text||"")], {type: `${type};charset=utf-8`});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename || "download.txt";
    document.body.appendChild(a);
    a.click();
    setTimeout(()=>{
      URL.revokeObjectURL(a.href);
      a.remove();
    }, 0);
  }

  TYBH.util = {
    $,
    escapeHtml, escapeAttr, stripBom,
    isoToday, ymToday, toMonth,
    num, money, uid, cleanCell,
    monthLabel, uniq,
    parseCsv, toCsv, toCsvNoBom,
    parseMaster, readKeyValueBlock,
    pickFirstMatch, parseKeywords, catMatches,
    startOfWeek, endOfWeek, isoToDate,
    daysLeftInMonth, daysInMonth,
    downloadText
  };
})();
